import { useCallback, useState } from 'react';

import { Braces, Check, Pencil, Search, Trash2, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useContacts, useDeleteContact, useUpdateContact } from '@/hooks/useContacts';

import type { Contact } from '@repo/shared-types';

const DataEditorDialog = ({
  contact,
  open,
  onOpenChange,
}: {
  contact: Contact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { toast } = useToast();
  const updateContact = useUpdateContact();
  const [json, setJson] = useState(() => JSON.stringify(contact.data ?? {}, null, 2));
  const [error, setError] = useState('');

  const handleSave = async () => {
    try {
      const parsed = JSON.parse(json);
      await updateContact.mutateAsync({ id: contact.id, data: { data: parsed } });
      toast({ title: 'Data updated' });
      onOpenChange(false);
    } catch (e) {
      if (e instanceof SyntaxError) {
        setError(`Invalid JSON: ${e.message}`);
      } else {
        toast({ title: 'Failed to update data', variant: 'destructive' });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-base">Edit Data &mdash; {contact.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <textarea
            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[300px] w-full rounded-md border px-3 py-2 font-mono text-sm focus-visible:ring-2 focus-visible:outline-none"
            value={json}
            onChange={(e) => {
              setJson(e.target.value);
              setError('');
            }}
            spellCheck={false}
          />
          {error && <p className="text-destructive text-xs">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={updateContact.isPending}>
            {updateContact.isPending ? 'Saving...' : 'Save Data'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditableRow = ({ contact, onCancel }: { contact: Contact; onCancel: () => void }) => {
  const { toast } = useToast();
  const updateContact = useUpdateContact();
  const [name, setName] = useState(contact.name);
  const [phone, setPhone] = useState(contact.phone ?? '');
  const [email, setEmail] = useState(contact.email ?? '');

  const handleSave = async () => {
    if (!name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    if (!phone.trim() && !email.trim()) {
      toast({ title: 'Phone or email is required', variant: 'destructive' });
      return;
    }
    try {
      await updateContact.mutateAsync({
        id: contact.id,
        data: {
          name: name.trim(),
          phone: phone.trim() || null,
          email: email.trim() || null,
        },
      });
      toast({ title: 'Contact updated' });
      onCancel();
    } catch {
      toast({ title: 'Failed to update contact', variant: 'destructive' });
    }
  };

  return (
    <TableRow>
      <TableCell>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-8 text-sm"
          autoFocus
        />
      </TableCell>
      <TableCell>
        <Input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="h-8 w-44 font-mono text-sm"
          placeholder="No phone"
        />
      </TableCell>
      <TableCell>
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-8 w-48 text-sm"
          placeholder="No email"
        />
      </TableCell>
      <TableCell />
      <TableCell>{new Date(contact.created_at as string).toLocaleDateString()}</TableCell>
      <TableCell>
        <div className="flex gap-1">
          <button
            onClick={handleSave}
            disabled={updateContact.isPending}
            className="rounded p-1 text-emerald-500 hover:text-emerald-400"
            title="Save"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground rounded p-1"
            title="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export const DealerKitContactsPage = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const deleteContact = useDeleteContact();

  const page = Number(searchParams.get('page')) || 1;
  const search = searchParams.get('search') || '';
  const [searchInput, setSearchInput] = useState(search);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dataEditContact, setDataEditContact] = useState<Contact | null>(null);

  const { data, isLoading } = useContacts({
    page,
    limit: 20,
    search: search || undefined,
  });

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!value) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        if (key !== 'page') next.delete('page');
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    const timeout = setTimeout(() => updateParam('search', value), 300);
    return () => clearTimeout(timeout);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete contact "${name}"? This cannot be undone.`)) return;
    try {
      await deleteContact.mutateAsync(id);
      toast({ title: 'Contact deleted' });
    } catch {
      toast({ title: 'Failed to delete contact', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-foreground text-2xl font-bold">Contacts</h1>
        <p className="text-muted-foreground text-sm">All contacts across the platform</p>
      </div>

      <div className="relative max-w-sm">
        <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder="Search by name, phone, or email..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : !data?.data.length ? (
        <p className="text-muted-foreground">No contacts found.</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.data.map((contact) =>
                editingId === contact.id ? (
                  <EditableRow
                    key={contact.id}
                    contact={contact}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <TableRow key={contact.id}>
                    <TableCell className="font-medium">{contact.name}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {contact.phone || <span className="text-muted-foreground/50">&mdash;</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {contact.email || <span className="text-muted-foreground/50">&mdash;</span>}
                    </TableCell>
                    <TableCell>
                      {contact.data ? (
                        <span className="text-muted-foreground truncate text-xs">
                          {Object.keys(contact.data).length} field
                          {Object.keys(contact.data).length !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50 text-xs">&mdash;</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(contact.created_at as string).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setEditingId(contact.id)}
                          className="text-muted-foreground hover:text-foreground rounded p-1"
                          title="Edit name & phone"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDataEditContact(contact)}
                          className="text-muted-foreground hover:text-foreground rounded p-1"
                          title="Edit JSON data"
                        >
                          <Braces className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(contact.id, contact.name)}
                          className="text-muted-foreground hover:text-destructive rounded p-1"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>

          {data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                Page {data.page} of {data.totalPages} ({data.total} contacts)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => updateParam('page', String(page - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= data.totalPages}
                  onClick={() => updateParam('page', String(page + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {dataEditContact && (
        <DataEditorDialog
          key={dataEditContact.id}
          contact={dataEditContact}
          open={!!dataEditContact}
          onOpenChange={(open) => !open && setDataEditContact(null)}
        />
      )}
    </div>
  );
};
